import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { api } from '../../../services/api'
import { useAuth } from '../../../contexts/AuthContext'
import type {
    OpportunityRedmineView,
    OpportunityTeamAlignment,
    RedmineIssueSummary,
    RedmineProjectSummary,
    TeamAlignmentCandidate,
    TeamAlignmentPick,
    TeamAlignmentSubproject,
    TeamAlignmentUnmapped,
} from '../../../types/redmine'

/**
 * Redmine side of an opportunity: a project picker (admins) plus the mirror
 * of the linked project — tasks, requests, hardware, members and hours —
 * with the diff between the Manager team and the Redmine memberships.
 *
 * Credentials are the corporate login (same LDAP as PlexTime); the backend
 * reuses the user:pass proven at sign-in, so nothing is configured here.
 */

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: #fafbfc;
  margin-top: ${({ theme }) => theme.spacing.sm};
`

const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
`

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const Warn = styled.div`
  background: #FFF8E1;
  border-left: 4px solid ${({ theme }) => theme.colors.warning};
  padding: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`

const Danger = styled(Warn)`
  background: #FFEBEE;
  border-left-color: ${({ theme }) => theme.colors.danger};
`

const SmallButton = styled.button<{ $variant?: 'primary' | 'ghost' | 'danger' }>`
  padding: 4px 10px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  border: 1px solid ${({ theme, $variant }) => ($variant === 'danger' ? theme.colors.danger : theme.colors.primary)};
  background: ${({ theme, $variant }) =>
        $variant === 'primary' ? theme.colors.primary : $variant === 'danger' ? '#FFEBEE' : 'white'};
  color: ${({ theme, $variant }) =>
        $variant === 'primary' ? 'white' : $variant === 'danger' ? theme.colors.danger : theme.colors.primary};
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const Results = styled.ul`
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: white;
  max-height: 220px;
  overflow-y: auto;
`

const ResultItem = styled.li<{ $disabled?: boolean }>`
  padding: 6px 10px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  display: flex;
  justify-content: space-between;
  gap: 8px;
  &:hover { background: ${({ theme }) => theme.colors.secondary}; }
`

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin: ${({ theme }) => theme.spacing.sm} 0;
  flex-wrap: wrap;
`

const Tab = styled.button<{ $active?: boolean }>`
  padding: 4px 10px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  border-radius: 999px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : 'white')};
  color: ${({ theme, $active }) => ($active ? 'white' : theme.colors.text)};
  cursor: pointer;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  th, td { padding: 4px 6px; border-bottom: 1px solid ${({ theme }) => theme.colors.border}; text-align: left; }
  th { background: ${({ theme }) => theme.colors.secondary}; font-weight: 600; }
`

const Stat = styled.div`
  display: inline-flex;
  flex-direction: column;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: white;
  min-width: 90px;
  strong { font-size: ${({ theme }) => theme.fontSizes.md}; color: ${({ theme }) => theme.colors.primary}; }
  span { font-size: 0.7rem; color: ${({ theme }) => theme.colors.textMuted}; }
`

const Chip = styled.span<{ $tone?: 'ok' | 'warn' | 'muted' }>`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 0.7rem;
  background: ${({ $tone }) => ($tone === 'ok' ? '#E8F5E9' : $tone === 'warn' ? '#FFF3E0' : '#EEEEEE')};
  color: ${({ $tone }) => ($tone === 'ok' ? '#2E7D32' : $tone === 'warn' ? '#E65100' : '#616161')};
`

const AlignBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: white;
`

const AlignHead = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  margin: 8px 0 4px;
`

const CellInput = styled.input`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  padding: 2px 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
`

type TabKey = 'tasks' | 'requests' | 'hardware' | 'members' | 'time'

/** What the alignment box lets the user change before adding someone. */
interface PickDraft {
    checked: boolean
    role: string
    load: number
}

const lastSegment = (name: string) => (name.includes(' - ') ? name.split(' - ').slice(-1)[0] : name)

interface Props {
    /** Existing opportunity id; the panel is idle for a brand-new opportunity. */
    opportunityId?: number
    /** Current value of the "Código Redmine" text field (kept in sync both ways). */
    identifier: string
    /** Called when the picker selects a project, so the modal's text field follows. */
    onIdentifierChange: (identifier: string, projectId?: number, name?: string) => void
    /** Hide the panel's own search box when the surrounding form already has the dropdown. */
    showPicker?: boolean
    /** Bump to make the panel reload (e.g. after the form's dropdown changed the link). */
    refreshKey?: number
    /**
     * 'Alinear con Redmine': adds the people picked to the form's project team
     * (the modal saves them like a member added by hand). Without it the button
     * is not offered.
     */
    onAddToTeam?: (picks: TeamAlignmentPick[]) => void
    /** Manager user ids already on the form's project team, which the box no longer proposes. */
    teamUserIds?: string[]
    /**
     * Move a project-team member's row to the (sub)project they log to (null:
     * the linked project itself). Without it the box does not offer it.
     */
    onRouteMember?: (userId: string, subproject: TeamAlignmentSubproject | null) => void
    /** The subproject each project-team member's row bills to now in the form (user id -> project id). */
    teamRouting?: Record<string, number | null>
}

const fmtHours = (h: number) => `${Math.round(h * 100) / 100} h`

const IssueTable = ({ issues }: { issues: RedmineIssueSummary[] }) => {
    if (issues.length === 0) return <Muted>Nada en esta categoría.</Muted>
    return (
        <Table>
            <thead>
                <tr>
                    <th>#</th><th>Asunto</th><th>Tracker</th><th>Estado</th><th>Asignado</th><th>%</th><th>Horas</th>
                </tr>
            </thead>
            <tbody>
                {issues.map((i) => (
                    <tr key={i.id} style={{ opacity: i.is_closed ? 0.6 : 1 }}>
                        <td>{i.url ? <a href={i.url} target="_blank" rel="noreferrer">#{i.id}</a> : `#${i.id}`}</td>
                        <td>{i.subject}</td>
                        <td>{i.tracker?.name || '—'}</td>
                        <td><Chip $tone={i.is_closed ? 'muted' : 'ok'}>{i.status?.name || '—'}</Chip></td>
                        <td>{i.assigned_to?.name || '—'}</td>
                        <td>{i.done_ratio}%</td>
                        <td>{fmtHours(i.spent_hours)}{i.estimated_hours ? ` / ${fmtHours(i.estimated_hours)}` : ''}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    )
}

export const RedmineOpportunityPanel = ({
    opportunityId, identifier, onIdentifierChange, showPicker = true, refreshKey = 0, onAddToTeam, teamUserIds = [],
    onRouteMember, teamRouting = {},
}: Props) => {
    const { user } = useAuth()
    const isAdmin = user?.role?.toLowerCase() === 'admin'
    const [view, setView] = useState<OpportunityRedmineView | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [unavailable, setUnavailable] = useState<string | null>(null)
    const [tab, setTab] = useState<TabKey>('tasks')
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<RedmineProjectSummary[] | null>(null)
    const [searching, setSearching] = useState(false)
    const [linking, setLinking] = useState(false)
    const [suggestions, setSuggestions] = useState<RedmineProjectSummary[] | null>(null)
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    // 'Alinear con Redmine'
    const [alignOpen, setAlignOpen] = useState(false)
    const [alignment, setAlignment] = useState<OpportunityTeamAlignment | null>(null)
    const [aligning, setAligning] = useState(false)
    const [alignError, setAlignError] = useState<string | null>(null)
    const [drafts, setDrafts] = useState<Record<number, PickDraft>>({})
    const [linkChoice, setLinkChoice] = useState<Record<number, string>>({})
    const [linkingUser, setLinkingUser] = useState<number | null>(null)

    const loadAlignment = useCallback(async () => {
        if (!opportunityId) return
        setAligning(true)
        setAlignError(null)
        try {
            const res = await api.redmine.getTeamAlignment(opportunityId)
            const data = res?.data as OpportunityTeamAlignment
            setAlignment(data)
            // Recent loggers are ticked; someone who only logged long ago is
            // offered unticked (old hours do not mean they are still on it).
            setDrafts(Object.fromEntries((data?.to_add || []).map((c) => [
                c.user_id, { checked: c.recent, role: c.suggested_role, load: c.suggested_load },
            ])))
        } catch (err: any) {
            setAlignError(err?.response?.data?.detail || 'No se pudo comparar el equipo con Redmine')
        } finally {
            setAligning(false)
        }
    }, [opportunityId])

    const toggleAlign = () => {
        const opening = !alignOpen
        setAlignOpen(opening)
        if (opening) loadAlignment()
    }

    const pending: TeamAlignmentCandidate[] = (alignment?.to_add || []).filter((c) => !teamUserIds.includes(String(c.user_id)))
    const picked = pending.filter((c) => drafts[c.user_id]?.checked)
    // Rows already moved in the form drop out of the proposals.
    const toReroute = (alignment?.reroute || []).filter(
        (r) => (teamRouting[String(r.user_id)] ?? null) !== (r.subproject?.id ?? null),
    )

    const addPicked = () => {
        if (!onAddToTeam || picked.length === 0) return
        onAddToTeam(picked.map((c) => ({
            userId: String(c.user_id),
            userName: c.display_name || c.redmine_name,
            role: (drafts[c.user_id]?.role || c.suggested_role).trim() || 'member',
            load: Math.max(0, Math.min(100, drafts[c.user_id]?.load ?? c.suggested_load)),
            subproject: c.subproject || null,
        })))
    }

    const linkUnmapped = async (person: TeamAlignmentUnmapped) => {
        const choice = linkChoice[person.redmine_user_id] ?? (person.suggested_user_id ? String(person.suggested_user_id) : '')
        if (!choice) return
        setLinkingUser(person.redmine_user_id)
        setAlignError(null)
        try {
            await api.redmine.setUserMapping(Number(choice), {
                redmine_user_id: person.redmine_user_id,
                redmine_name: person.redmine_name,
            })
            await loadAlignment()
            await load()
        } catch (err: any) {
            setAlignError(err?.response?.data?.detail || 'No se pudo vincular el usuario')
        } finally {
            setLinkingUser(null)
        }
    }

    const load = useCallback(async () => {
        if (!opportunityId) return
        setLoading(true)
        setError(null)
        try {
            const res = await api.redmine.getOpportunity(opportunityId)
            const data = res?.data as OpportunityRedmineView | null
            setView(data && typeof data === 'object' && 'linked' in data ? data : null)
            setUnavailable(null)
        } catch (err: any) {
            const status = err?.response?.status
            const detail = err?.response?.data?.detail
            if (status === 503) setUnavailable(detail || 'Redmine no está configurado')
            else if (status === 502) setUnavailable(detail || 'Redmine no responde')
            else setError(detail || 'No se pudo cargar la información de Redmine')
        } finally {
            setLoading(false)
        }
    }, [opportunityId])

    useEffect(() => {
        load()
    }, [load, refreshKey])

    useEffect(() => {
        if (!opportunityId || !isAdmin || !showPicker) { setSuggestions(null); return }
        let cancelled = false
        api.redmine.suggestProjects(opportunityId, '', 6)
            .then((res) => { if (!cancelled) setSuggestions(Array.isArray(res?.data) ? res.data : []) })
            .catch(() => { if (!cancelled) setSuggestions([]) })
        return () => { cancelled = true }
    }, [opportunityId, isAdmin])

    const runSearch = (q: string) => {
        setQuery(q)
        if (searchTimer.current) clearTimeout(searchTimer.current)
        if (!q.trim()) { setResults(null); return }
        searchTimer.current = setTimeout(async () => {
            setSearching(true)
            try {
                // Ranked against this opportunity when it exists; plain search for a new one.
                const res = opportunityId
                    ? await api.redmine.suggestProjects(opportunityId, q.trim(), 20)
                    : await api.redmine.searchProjects(q.trim(), 20)
                setResults(Array.isArray(res?.data) ? res.data : [])
            } catch (err: any) {
                setResults([])
                setUnavailable(err?.response?.data?.detail || 'Redmine no responde')
            } finally {
                setSearching(false)
            }
        }, 300)
    }

    const pick = async (p: RedmineProjectSummary) => {
        if (p.linked_opportunity_id && p.linked_opportunity_id !== opportunityId) return
        onIdentifierChange(p.identifier, p.id, p.name)
        setResults(null)
        setQuery('')
        if (opportunityId && isAdmin) {
            setLinking(true)
            try {
                await api.redmine.linkOpportunity(opportunityId, { project_id: p.id })
                await load()
            } catch (err: any) {
                setError(err?.response?.data?.detail || 'No se pudo enlazar el proyecto')
            } finally {
                setLinking(false)
            }
        }
    }

    const unlink = async () => {
        if (!opportunityId) return
        if (!window.confirm('¿Quitar el enlace con el proyecto de Redmine? (no borra nada en Redmine)')) return
        setLinking(true)
        try {
            await api.redmine.unlinkOpportunity(opportunityId)
            onIdentifierChange('')
            await load()
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'No se pudo desenlazar')
        } finally {
            setLinking(false)
        }
    }

    const ov = view?.overview || null
    const counts = ov?.issue_counts || {}

    return (
        <Panel>
            <Row style={{ justifyContent: 'space-between' }}>
                <strong style={{ fontSize: '0.9rem' }}>🔗 Redmine</strong>
                <Row>
                    {ov && (
                        <>
                            <a href={ov.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem' }}>
                                Abrir {ov.identifier} ↗
                            </a>
                            {ov.issues_url && (
                                <a href={ov.issues_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem' }}>
                                    Tareas en Redmine ↗
                                </a>
                            )}
                            {ov.time_entries_url && (
                                <a href={ov.time_entries_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem' }}>
                                    Imputaciones en Redmine ↗
                                </a>
                            )}
                        </>
                    )}
                    {opportunityId && onAddToTeam && ov && (
                        <SmallButton
                            type="button"
                            $variant={alignOpen ? 'primary' : undefined}
                            onClick={toggleAlign}
                            aria-expanded={alignOpen}
                            title="Compara el Equipo Proyecto con quien imputa horas en este proyecto de Redmine"
                        >
                            👥 Alinear con Redmine
                        </SmallButton>
                    )}
                    {opportunityId && <SmallButton type="button" onClick={load} disabled={loading}>🔄</SmallButton>}
                    {opportunityId && isAdmin && view?.linked && (
                        <SmallButton type="button" $variant="danger" onClick={unlink} disabled={linking}>Desenlazar</SmallButton>
                    )}
                </Row>
            </Row>

            {isAdmin && showPicker && (
                <div style={{ marginTop: 8 }}>
                    <Row>
                        <SearchInput
                            type="text"
                            placeholder="Buscar proyecto en Redmine (nombre o identificador)…"
                            value={query}
                            onChange={(e) => runSearch(e.target.value)}
                        />
                        {searching && <Muted>buscando…</Muted>}
                    </Row>
                    {results && (
                        <Results>
                            {results.length === 0 && <ResultItem $disabled>Sin resultados</ResultItem>}
                            {results.map((p) => {
                                const taken = !!p.linked_opportunity_id && p.linked_opportunity_id !== opportunityId
                                return (
                                    <ResultItem key={p.id} $disabled={taken} onClick={() => pick(p)} title={taken ? `Ya enlazado a la oportunidad #${p.linked_opportunity_id}` : ''}>
                                        <span>
                                            <strong>{p.name}</strong> <Muted>({p.identifier}{p.parent ? ` · ${p.parent.name}` : ''})</Muted>
                                        </span>
                                        <span>
                                            {p.status !== 1 && <Chip $tone="muted">cerrado</Chip>}
                                            {taken && <Chip $tone="warn">enlazado #{p.linked_opportunity_id}</Chip>}
                                        </span>
                                    </ResultItem>
                                )
                            })}
                        </Results>
                    )}
                    {!opportunityId && identifier && (
                        <Muted>Se enlazará «{identifier}» al guardar la oportunidad.</Muted>
                    )}
                    {opportunityId && !view?.linked && !query && suggestions && suggestions.length > 0 && (
                        <Results>
                            <ResultItem $disabled><Muted>Sugeridos para esta oportunidad (leídos de Redmine)</Muted></ResultItem>
                            {suggestions.map((p) => (
                                <ResultItem key={`s-${p.id}`} onClick={() => pick(p)}>
                                    <span><strong>{p.name}</strong> <Muted>({p.identifier})</Muted></span>
                                    <Muted>{(p as any).reasons?.join(' · ')}</Muted>
                                </ResultItem>
                            ))}
                        </Results>
                    )}
                </div>
            )}

            {!opportunityId && <Muted>Guarda la oportunidad para ver su proyecto de Redmine.</Muted>}
            {unavailable && <Warn style={{ marginTop: 8 }}>⚠️ {unavailable}</Warn>}
            {error && <Danger style={{ marginTop: 8 }}>{error}</Danger>}
            {loading && <Muted>Cargando Redmine…</Muted>}

            {opportunityId && view && !view.linked && !loading && !unavailable && (
                <Muted>
                    {view.error
                        ? `⚠️ ${view.error}`
                        : identifier
                            ? `El código «${identifier}» aún no está resuelto en Redmine.`
                            : 'Esta oportunidad no está enlazada a ningún proyecto de Redmine.'}
                </Muted>
            )}

            {/* Linked, but Redmine could not be read (401, project gone…): the
                backend keeps linked=true (so Desenlazar stays) and reports the
                reason only in view.error, with no overview. Without this the
                panel showed nothing at all. */}
            {opportunityId && view?.linked && !ov && view.error && !loading && !unavailable && (
                <Warn style={{ marginTop: 8 }}>
                    ⚠️ {view.error}
                    {(view.redmine_project_name || view.redmine_identifier) && (
                        <Muted> (enlazada a {view.redmine_project_name || view.redmine_identifier})</Muted>
                    )}
                </Warn>
            )}

            {ov && (
                <>
                    <Row style={{ marginTop: 8 }}>
                        <Stat><strong>{counts.tasks_open ?? 0}/{counts.tasks_total ?? 0}</strong><span>tareas abiertas</span></Stat>
                        <Stat><strong>{counts.requests_open ?? 0}/{counts.requests_total ?? 0}</strong><span>peticiones/compras abiertas</span></Stat>
                        <Stat><strong>{ov.hardware.length}</strong><span>hardware</span></Stat>
                        <Stat><strong>{ov.members.length}</strong><span>miembros</span></Stat>
                        <Stat><strong>{fmtHours(ov.time.total_hours)}</strong><span>imputadas</span></Stat>
                        {(ov.start_date || ov.due_date) && (
                            <Stat><strong style={{ fontSize: '0.8rem' }}>{ov.start_date || '?'} → {ov.due_date || '?'}</strong><span>fechas Redmine</span></Stat>
                        )}
                    </Row>
                    <Muted style={{ display: 'block', marginTop: 6 }}>
                        Leído de Redmine como <strong>{ov.viewer_login || '—'}</strong>
                        {ov.viewer_roles && ov.viewer_roles.length > 0 ? ` (${ov.viewer_roles.join(', ')})` : ' (sin membresía en este proyecto)'}
                        {' · '}trackers del proyecto: {ov.trackers.length > 0 ? ov.trackers.map((t) => t.name).join(', ') : 'ninguno'}
                    </Muted>
                    {ov.warnings.length > 0 && <Warn style={{ marginTop: 8 }}>{ov.warnings.join(' · ')}</Warn>}
                    {(view!.team_only_in_manager.length > 0 || view!.team_only_in_redmine.length > 0) && (
                        <Warn style={{ marginTop: 8 }}>
                            {view!.team_only_in_manager.length > 0 && (
                                <div>
                                    👤 En el equipo del Manager sin acceso en Redmine (no son miembros del proyecto ni de sus subproyectos, ni imputan):{' '}
                                    {view!.team_only_in_manager.map((t) => `${t.display_name || t.username}${t.redmine_mapped ? '' : ' (sin mapear)'}`).join(', ')}
                                </div>
                            )}
                            {view!.team_only_in_redmine.length > 0 && (
                                <div>
                                    👤 Con acceso en Redmine pero sin asignación en el Manager:{' '}
                                    {view!.team_only_in_redmine.map((m) => m.name).join(', ')}
                                </div>
                            )}
                        </Warn>
                    )}
                    {alignOpen && (
                        <AlignBox aria-label="Alinear el equipo con Redmine">
                            <Row style={{ justifyContent: 'space-between' }}>
                                <strong style={{ fontSize: '0.8rem' }}>👥 Equipo Proyecto frente a quien imputa en Redmine</strong>
                                {alignment?.recent_from && (
                                    <Muted>Carga estimada con las horas desde el {alignment.recent_from} (8 semanas)</Muted>
                                )}
                            </Row>
                            {aligning && <Muted>Comparando con Redmine…</Muted>}
                            {alignError && <Danger style={{ marginTop: 6 }}>{alignError}</Danger>}
                            {alignment?.error && <Warn style={{ marginTop: 6 }}>⚠️ {alignment.error}</Warn>}

                            {pending.length > 0 && (
                                <>
                                    <AlignHead>Imputan y no están en el Equipo Proyecto</AlignHead>
                                    <Table>
                                        <thead>
                                            <tr><th /><th>Persona</th><th>Horas (8 sem. / total)</th><th>Rol</th><th>Carga</th><th>Subproyecto</th></tr>
                                        </thead>
                                        <tbody>
                                            {pending.map((c) => {
                                                const draft = drafts[c.user_id] || { checked: c.recent, role: c.suggested_role, load: c.suggested_load }
                                                const setDraft = (patch: Partial<PickDraft>) =>
                                                    setDrafts((prev) => ({ ...prev, [c.user_id]: { ...draft, ...patch } }))
                                                const name = c.display_name || c.redmine_name
                                                return (
                                                    <tr key={c.user_id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={draft.checked}
                                                                onChange={(e) => setDraft({ checked: e.target.checked })}
                                                                aria-label={`Añadir a ${name}`}
                                                            />
                                                        </td>
                                                        <td>
                                                            {name}
                                                            {c.display_name && <Muted> ({c.redmine_name})</Muted>}
                                                            {' '}{c.in_oferta_team && <Chip $tone="muted">equipo oferta</Chip>}
                                                            {' '}{!c.recent && <Chip $tone="warn">sin imputar en 8 semanas</Chip>}
                                                        </td>
                                                        <td>{fmtHours(c.hours_recent)} / {fmtHours(c.hours_total)}</td>
                                                        <td>
                                                            <CellInput
                                                                value={draft.role}
                                                                onChange={(e) => setDraft({ role: e.target.value })}
                                                                aria-label={`Rol de ${name}`}
                                                                style={{ width: 150 }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <CellInput
                                                                type="number"
                                                                min={0}
                                                                max={100}
                                                                value={draft.load}
                                                                onChange={(e) => setDraft({ load: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
                                                                aria-label={`Carga de ${name}`}
                                                                style={{ width: 52 }}
                                                            /> %
                                                        </td>
                                                        <td>{c.subproject ? lastSegment(c.subproject.name) : 'proyecto global'}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </Table>
                                    <Row style={{ marginTop: 6 }}>
                                        <SmallButton type="button" $variant="primary" disabled={picked.length === 0} onClick={addPicked}>
                                            Añadir {picked.length} al Equipo Proyecto
                                        </SmallButton>
                                        <Muted>Entran desde hoy (la fecha se cambia en su fila) y se guardan con la oportunidad.</Muted>
                                    </Row>
                                </>
                            )}

                            {(alignment?.unmapped.length ?? 0) > 0 && (
                                <>
                                    <AlignHead>Imputan sin usuario del Manager vinculado</AlignHead>
                                    {alignment!.unmapped.map((u) => {
                                        const choice = linkChoice[u.redmine_user_id] ?? (u.suggested_user_id ? String(u.suggested_user_id) : '')
                                        return (
                                            <Row key={u.redmine_user_id} style={{ marginBottom: 4 }}>
                                                <span style={{ fontSize: '0.75rem' }}>
                                                    {u.redmine_name} <Muted>({fmtHours(u.hours_recent)} en 8 sem. / {fmtHours(u.hours_total)})</Muted>
                                                </span>
                                                {isAdmin ? (
                                                    <>
                                                        <select
                                                            value={choice}
                                                            onChange={(e) => setLinkChoice((prev) => ({ ...prev, [u.redmine_user_id]: e.target.value }))}
                                                            aria-label={`Usuario del Manager para ${u.redmine_name}`}
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            <option value="">Elegir persona del Manager…</option>
                                                            {alignment!.unlinked_users.map((m) => (
                                                                <option key={m.user_id} value={m.user_id}>
                                                                    {m.display_name || `#${m.user_id}`}{m.in_team ? ' (en el equipo)' : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <SmallButton
                                                            type="button"
                                                            disabled={!choice || linkingUser !== null}
                                                            onClick={() => linkUnmapped(u)}
                                                        >
                                                            {linkingUser === u.redmine_user_id ? 'Vinculando…' : 'Vincular'}
                                                        </SmallButton>
                                                    </>
                                                ) : (
                                                    <Muted>Un administrador puede vincularlo.</Muted>
                                                )}
                                            </Row>
                                        )
                                    })}
                                </>
                            )}

                            {onRouteMember && toReroute.length > 0 && (
                                <>
                                    <AlignHead>Imputan sobre todo en otro subproyecto que el de su fila</AlignHead>
                                    {toReroute.map((r) => {
                                        const where = r.subproject ? lastSegment(r.subproject.name) : 'proyecto global'
                                        return (
                                            <Row key={r.user_id} style={{ marginBottom: 4 }}>
                                                <span style={{ fontSize: '0.75rem' }}>
                                                    {r.display_name || `#${r.user_id}`}: imputa en «{where}» ({fmtHours(r.hours)}); su fila está en «{r.current_subproject ? lastSegment(r.current_subproject) : 'proyecto global'}»
                                                </span>
                                                <SmallButton type="button" onClick={() => onRouteMember(String(r.user_id), r.subproject || null)}>
                                                    Pasar a «{where}»
                                                </SmallButton>
                                            </Row>
                                        )
                                    })}
                                </>
                            )}

                            {(alignment?.idle.length ?? 0) > 0 && (
                                <>
                                    <AlignHead>En el Equipo Proyecto sin imputar en estas 8 semanas</AlignHead>
                                    <Muted>
                                        {alignment!.idle.map((m) => {
                                            const detail = !m.redmine_mapped
                                                ? 'sin vincular en Redmine'
                                                : m.last_spent_on ? `última imputación ${m.last_spent_on}` : 'nunca ha imputado aquí'
                                            return `${m.display_name || `#${m.user_id}`} (${detail})`
                                        }).join(' · ')}
                                    </Muted>
                                </>
                            )}

                            {alignment && !aligning && !alignment.error && pending.length === 0
                                && alignment.unmapped.length === 0 && alignment.idle.length === 0 && toReroute.length === 0 && (
                                <Muted style={{ display: 'block', marginTop: 6 }}>
                                    ✅ El Equipo Proyecto ya coincide con quien imputa en Redmine.
                                </Muted>
                            )}
                        </AlignBox>
                    )}
                    <Tabs>
                        <Tab type="button" $active={tab === 'tasks'} onClick={() => setTab('tasks')}>Tareas ({ov.tasks.length})</Tab>
                        <Tab type="button" $active={tab === 'requests'} onClick={() => setTab('requests')}>Peticiones y compras ({ov.requests.length})</Tab>
                        <Tab type="button" $active={tab === 'hardware'} onClick={() => setTab('hardware')}>Hardware ({ov.hardware.length})</Tab>
                        <Tab type="button" $active={tab === 'members'} onClick={() => setTab('members')}>Miembros ({ov.members.length})</Tab>
                        <Tab type="button" $active={tab === 'time'} onClick={() => setTab('time')}>Horas</Tab>
                    </Tabs>
                    {tab === 'tasks' && (
                        <>
                            {ov.tasks.length === 0 && ov.other_issues.length === 0 && (
                                <Warn>
                                    Redmine no devuelve ninguna tarea (ni abierta ni cerrada) en «{ov.identifier}» para {ov.viewer_login || 'este usuario'}.
                                    {!ov.trackers.some((t) => t.bucket === 'tasks') && ' El proyecto no tiene habilitado un tracker de tareas.'}
                                    {ov.issues_url && <> <a href={ov.issues_url} target="_blank" rel="noreferrer">Comprobar en Redmine ↗</a></>}
                                </Warn>
                            )}
                            <IssueTable issues={ov.tasks} />
                            {ov.other_issues.length > 0 && (
                                <details style={{ marginTop: 6 }}>
                                    <summary style={{ fontSize: '0.75rem', cursor: 'pointer' }}>Otros trackers ({ov.other_issues.length})</summary>
                                    <IssueTable issues={ov.other_issues} />
                                </details>
                            )}
                        </>
                    )}
                    {tab === 'requests' && (
                        <>
                            {ov.requests.length === 0 && (
                                <Warn>
                                    {ov.trackers.some((t) => t.bucket === 'requests')
                                        ? <>Redmine no devuelve ninguna petición en «{ov.identifier}» para {ov.viewer_login || 'este usuario'}.</>
                                        : <>El proyecto «{ov.identifier}» no tiene habilitado ningún tracker de peticiones (Petición, Solicitud, Soporte…): sus trackers son {ov.trackers.map((t) => t.name).join(', ') || 'ninguno'}.</>}
                                    {ov.issues_url && <> <a href={ov.issues_url} target="_blank" rel="noreferrer">Comprobar en Redmine ↗</a></>}
                                </Warn>
                            )}
                            <IssueTable issues={ov.requests} />
                        </>
                    )}
                    {tab === 'hardware' && (
                        <>
                            {ov.hardware.length === 0 && <Muted>Sin hardware en Redmine (issues de Materiales/Compras, Instalaciones o tracker/categoría/campo «hardware»).</Muted>}
                            {ov.hardware.length > 0 && (
                                <Table>
                                    <thead><tr><th>Origen</th><th>Nombre</th><th>Valor / Estado</th><th>Asignado</th></tr></thead>
                                    <tbody>
                                        {ov.hardware.map((h, idx) => (
                                            <tr key={`${h.kind}-${h.id ?? idx}`}>
                                                <td><Chip $tone="muted">{h.kind === 'issue' ? 'tarea' : h.kind === 'custom_field' ? 'campo' : 'categoría'}</Chip></td>
                                                <td>{h.url ? <a href={h.url} target="_blank" rel="noreferrer">{h.name}</a> : h.name}</td>
                                                <td>{h.value || h.status || '—'}</td>
                                                <td>{h.assigned_to || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                            {view!.manager_infra.length > 0 && (
                                <Muted style={{ display: 'block', marginTop: 6 }}>
                                    Infraestructura asignada en el Manager:{' '}
                                    {view!.manager_infra.map((i) => String(i.resource_name || i.partition_name || i.assignment_id)).join(', ')}
                                </Muted>
                            )}
                        </>
                    )}
                    {tab === 'members' && (
                        <Table>
                            <thead><tr><th>Miembro</th><th>Tipo</th><th>Roles</th><th>Manager</th></tr></thead>
                            <tbody>
                                {ov.members.map((m) => (
                                    <tr key={m.membership_id || `${m.kind}-${m.principal_id}`}>
                                        <td>{m.name}</td>
                                        <td>{m.kind === 'group' ? 'grupo' : 'usuario'}</td>
                                        <td>{m.roles.join(', ')}</td>
                                        <td>
                                            {m.kind === 'group' ? '—' : m.in_manager_team ? <Chip $tone="ok">en equipo</Chip> : m.manager_user_id ? <Chip $tone="warn">mapeado, no asignado</Chip> : <Chip $tone="muted">sin mapear</Chip>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    {tab === 'time' && (
                        <>
                            <Table>
                                <thead><tr><th>Persona</th><th>Horas</th><th>Entradas</th><th>Última</th></tr></thead>
                                <tbody>
                                    {ov.time.by_user.map((u) => (
                                        <tr key={u.redmine_user_id}>
                                            <td>{u.name}</td><td>{fmtHours(u.hours)}</td><td>{u.entries}</td><td>{u.last_spent_on || '—'}</td>
                                        </tr>
                                    ))}
                                    {ov.time.by_user.length === 0 && (
                                        <tr>
                                            <td colSpan={4}>
                                                <Muted>
                                                    Redmine no devuelve ninguna imputación en «{ov.identifier}» para {ov.viewer_login || 'este usuario'}.
                                                    {ov.time_entries_url && <> <a href={ov.time_entries_url} target="_blank" rel="noreferrer">Comprobar en Redmine ↗</a></>}
                                                </Muted>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                            {Object.keys(ov.time.by_month).length > 0 && (
                                <Muted style={{ display: 'block', marginTop: 6 }}>
                                    Por mes: {Object.entries(ov.time.by_month).map(([m, h]) => `${m}: ${fmtHours(h)}`).join(' · ')}
                                </Muted>
                            )}
                        </>
                    )}
                </>
            )}
        </Panel>
    )
}

export default RedmineOpportunityPanel
